import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  getCallRecording, startCallRecording, type CallRecordingOut, type RecordingStatus,
} from '../../api/callRecording';
import { subscribeChatWs } from '../chat/chatWsBus';

/**
 * How far along a recording is. Statuses only move forward, so a GET that
 * resolves after a newer `call_recording` frame can never pull the badge back.
 */
const STATUS_RANK: Record<RecordingStatus, number> = {
  pending: 0,
  starting: 1,
  recording: 2,
  processing: 3,
  ready: 4,
  failed: 4,
  unavailable: 4,
  not_started: 4,
  deleted: 4,
};

/**
 * A live recording that turns to `processing` while the call carries on was
 * stopped by an admin (or the safety cap). Hanging up produces the same
 * transition a moment before the call itself ends, so wait this long and only
 * announce the stop if the call is still live.
 */
export const RECORDING_STOP_TOAST_DELAY_MS = 1500;

type RecordingListener = (recording: CallRecordingOut) => void;
const localListeners = new Set<RecordingListener>();

/**
 * Hand a recording this device got over REST (e.g. the reply to Start
 * recording) to every `useCallRecording` showing that call. It goes through
 * the same forward-only rule as a `call_recording` frame, so a reply that
 * lands after a newer frame cannot pull the status back.
 */
export function applyCallRecording(recording: CallRecordingOut | null | undefined): void {
  if (!recording) return;
  localListeners.forEach(listener => {
    try { listener(recording); } catch { /* recording must never affect the call */ }
  });
}

/**
 * Start recording ("During call" mode). Success needs no toast: the REC badge
 * appears through the usual `call_recording` frame (and the reply is applied
 * here too). A refusal shows the server's reason; the call is never touched.
 */
export async function startRecordingForCall(callId: string): Promise<void> {
  try {
    applyCallRecording(await startCallRecording(callId));
  } catch (error: any) {
    const detail = error?.response?.data?.detail;
    toast.error(typeof detail === 'string' ? detail
      : typeof detail?.message === 'string' ? detail.message
      : 'Could not start recording. Try again.');
  }
}

/**
 * Recording state for the call on screen: fetched when the call appears,
 * refreshed on socket reconnect, and kept current by `call_recording` frames.
 * Every failure here is swallowed — recording must never affect the call.
 *
 * `live` is whether the call is in progress on this device (for the
 * mid-call "failed" / "stopped" toasts; nothing is announced while ringing).
 */
export function useCallRecording(callId: string | null, live: boolean): CallRecordingOut | null {
  const [recording, setRecording] = useState<CallRecordingOut | null>(null);
  const liveRef = useRef(live);
  liveRef.current = live;

  useEffect(() => {
    setRecording(null);
    if (!callId) return undefined;
    let disposed = false;
    let current: CallRecordingOut | null = null;
    let stopTimer: ReturnType<typeof setTimeout> | null = null;

    const apply = (next: CallRecordingOut | null) => {
      if (disposed || !next || next.call_id !== callId) return;
      const previous = current;
      if (previous && previous.id === next.id && STATUS_RANK[next.status] < STATUS_RANK[previous.status]) return;
      current = next;
      setRecording(next);
      if (!previous || previous.status === next.status || !liveRef.current) return;
      if (next.status === 'failed') {
        toast.error('Recording failed — the call continues');
      } else if (previous.status === 'recording' && next.status === 'processing'
        && (next.reason === 'disabled' || next.reason === 'participant_app_outdated')) {
        // The server says outright why it stopped mid-call (admin turned
        // recording off, or someone without the notice joined).
        if (stopTimer) clearTimeout(stopTimer);
        stopTimer = null;
        toast('Recording stopped');
      } else if (previous.status === 'recording' && next.status === 'processing' && !stopTimer) {
        stopTimer = setTimeout(() => {
          stopTimer = null;
          if (!disposed && liveRef.current) toast('Recording stopped');
        }, RECORDING_STOP_TOAST_DELAY_MS);
      }
    };

    const load = () => {
      // Started inside a promise so even a synchronous throw stays contained.
      Promise.resolve()
        .then(() => getCallRecording(callId))
        .then(apply)
        .catch(() => { /* a later frame or reconnect retries */ });
    };
    load();

    const unsubscribe = subscribeChatWs((evt) => {
      if (evt?.type === 'call_recording' && evt.call_id === callId && evt.recording) apply(evt.recording);
      else if (evt?.type === 'ws_connected') load();
    });
    localListeners.add(apply);

    return () => {
      disposed = true;
      unsubscribe();
      localListeners.delete(apply);
      if (stopTimer) clearTimeout(stopTimer);
    };
  }, [callId]);

  return recording;
}
